import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { RoleBadge } from "@/components/common";
import api, { money } from "@/lib/api";
import { SignOut, Wallet, UserCircle } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export default function Header() {
  const { user, logout } = useAuth();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const showBalance = user?.role === "customer" || user?.role === "reseller";
  const [balanceMinor, setBalanceMinor] = useState(null);
  // Match AI campaign $2 floor — at or below $2.00 is a low-balance warning.
  const LOW_BALANCE_MINOR = 200;
  const isLowBalance = balanceMinor != null && balanceMinor <= LOW_BALANCE_MINOR;

  const loadBalance = useCallback(async () => {
    if (!showBalance) return;
    try {
      const { data } = await api.get("/billing/balance");
      setBalanceMinor(data?.cash_balance_minor ?? 0);
    } catch {
      /* keep last known balance */
    }
  }, [showBalance]);

  useEffect(() => {
    if (!showBalance) {
      setBalanceMinor(null);
      return undefined;
    }
    loadBalance();
    const id = setInterval(loadBalance, 30000);
    const onFocus = () => loadBalance();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [showBalance, loadBalance]);

  return (
    <header className="sticky top-0 z-50 h-16 border-b border-border/40 bg-background/70 backdrop-blur-xl flex items-center px-6">
      <div className="ml-auto flex items-center gap-1.5">
        {showBalance && (
          <Link
            to="/billing"
            title={isLowBalance ? "Low balance — add cash" : "Cash balance"}
            data-testid="header-balance"
            className={`relative flex items-center gap-1.5 h-9 px-2.5 mr-0.5 rounded-full border text-sm tabular-nums transition-colors ${
              isLowBalance
                ? "border-red-500/50 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/60"
                : "border-border/60 bg-muted/40 hover:bg-muted"
            }`}
          >
            {isLowBalance && (
              <span
                className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                data-testid="header-balance-low-dot"
                aria-label="Low balance"
                title="Low balance"
              >
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-background header-balance-blink" />
              </span>
            )}
            <Wallet
              className={`w-4 h-4 shrink-0 ${
                isLowBalance
                  ? "text-red-600 dark:text-red-400"
                  : "text-emerald-600 dark:text-emerald-400"
              }`}
              weight="duotone"
            />
            <span
              className={`font-medium ${
                isLowBalance ? "text-red-600 dark:text-red-400" : "text-foreground"
              }`}
            >
              {balanceMinor == null ? "…" : money(balanceMinor)}
            </span>
          </Link>
        )}
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2.5 pl-1.5 pr-3 h-10 rounded-full hover:bg-muted transition-colors duration-200" data-testid="profile-menu-btn">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left leading-tight">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground">{user?.email}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>My Account</span>
              <RoleBadge role={user?.role} />
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" data-testid="profile-link" className="cursor-pointer">
                <UserCircle className="w-4 h-4 mr-2" weight="duotone" /> Personal profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
              <SignOut className="w-4 h-4 mr-2" weight="duotone" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
